import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { api, resolveImageUrl } from '../lib/api';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Link, useNavigate } from 'react-router-dom';
import {
  MessageCircle, Heart, Send, Trash2, ChevronDown,
  User, Briefcase, MapPin, Clock, MessageSquare,
  Lightbulb, TrendingUp, HelpCircle, PenLine,
  Share2, UserPlus, ExternalLink, Check, X, Users, Loader2, Users2
} from 'lucide-react';
import { toast } from 'sonner';

const POST_TYPES = [
  { id: 'update', label: 'Update', icon: PenLine, color: 'bg-blue-100 text-blue-700' },
  { id: 'promotion', label: 'Promotion', icon: TrendingUp, color: 'bg-green-100 text-green-700' },
  { id: 'question', label: 'Question', icon: HelpCircle, color: 'bg-amber-100 text-amber-700' },
  { id: 'tip', label: 'Pro Tip', icon: Lightbulb, color: 'bg-purple-100 text-purple-700' },
  { id: 'share_card', label: 'Shared Card', icon: Share2, color: 'bg-teal-100 text-teal-700' },
  { id: 'group_promotion', label: 'New Group', icon: Users2, color: 'bg-violet-100 text-violet-700' },
];

const timeAgo = (dateStr) => {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return days === 1 ? 'yesterday' : `${days}d ago`;
};

// ==================== SHARED CARD EMBED ====================
const SharedCardEmbed = ({ card }) => {
  if (!card) return null;
  return (
    <div className="mx-4 mb-3 border border-border rounded-lg bg-muted/30 overflow-hidden" data-testid={`shared-card-${card.user_id}`}>
      <div className="flex items-center gap-3 p-3">
        <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-primary/60 to-primary flex items-center justify-center flex-shrink-0 overflow-hidden">
          {card.photo ? (
            <img src={resolveImageUrl(card.photo)} alt="" className="w-full h-full object-cover" />
          ) : (
            <span className="text-primary-foreground text-lg font-bold">{(card.name || 'U')[0]}</span>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <Link to={`/card/${card.user_id}`} className="text-sm font-semibold hover:text-primary transition-colors" data-testid={`shared-card-name-${card.user_id}`}>
            {card.name}
          </Link>
          <p className="text-xs text-primary/80 font-medium">{card.profession}</p>
          {card.company && <p className="text-[11px] text-muted-foreground">{card.company}</p>}
          {card.city && <p className="text-[10px] text-muted-foreground">{card.city}, {card.state}</p>}
        </div>
        <Link to={`/card/${card.user_id}`} className="text-xs text-primary hover:underline flex items-center gap-1" data-testid={`view-card-btn-${card.user_id}`}>
          <ExternalLink className="w-3 h-3" />View Card
        </Link>
      </div>
    </div>
  );
};

// ==================== FEED TAB ====================
const FeedTab = ({ onStartChat }) => {
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [content, setContent] = useState('');
  const [postType, setPostType] = useState('update');
  const [posting, setPosting] = useState(false);
  const [expandedComments, setExpandedComments] = useState({});
  const [comments, setComments] = useState({});
  const [commentText, setCommentText] = useState({});
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareSearch, setShareSearch] = useState('');
  const [shareResults, setShareResults] = useState([]);

  const fetchFeed = useCallback(async (p = 1) => {
    try {
      const data = await api.getFeed(p);
      if (p === 1) setPosts(data.posts || []);
      else setPosts(prev => [...prev, ...(data.posts || [])]);
      setHasMore(data.has_more);
      setPage(p);
    } catch { toast.error('Failed to load feed'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchFeed(); }, [fetchFeed]);

  const handlePost = async () => {
    if (!content.trim()) return;
    setPosting(true);
    try {
      const newPost = await api.createFeedPost(content, postType);
      setPosts(prev => [newPost, ...prev]);
      setContent('');
      toast.success('Post shared!');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to post');
    } finally { setPosting(false); }
  };

  const handleLike = async (postId) => {
    try {
      const { liked } = await api.toggleLikePost(postId);
      setPosts(prev => prev.map(p => p.id === postId ? {
        ...p,
        likes: liked ? [...(p.likes || []), user?.id] : (p.likes || []).filter(l => l !== user?.id)
      } : p));
    } catch {}
  };

  const toggleComments = async (postId) => {
    const isOpen = expandedComments[postId];
    setExpandedComments(prev => ({ ...prev, [postId]: !isOpen }));
    if (!isOpen && !comments[postId]) {
      try {
        const data = await api.getComments(postId);
        setComments(prev => ({ ...prev, [postId]: data.comments || [] }));
      } catch {}
    }
  };

  const handleComment = async (postId) => {
    const text = commentText[postId]?.trim();
    if (!text) return;
    try {
      const comment = await api.addComment(postId, text);
      setComments(prev => ({ ...prev, [postId]: [...(prev[postId] || []), comment] }));
      setPosts(prev => prev.map(p => p.id === postId ? { ...p, comment_count: (p.comment_count || 0) + 1 } : p));
      setCommentText(prev => ({ ...prev, [postId]: '' }));
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to comment'); }
  };

  const handleDelete = async (postId) => {
    try {
      await api.deleteFeedPost(postId);
      setPosts(prev => prev.filter(p => p.id !== postId));
      toast.success('Post deleted');
    } catch { toast.error('Failed to delete'); }
  };

  const handleConnect = async (userId, userName) => {
    try {
      const res = await api.sendConnectionRequest(userId);
      if (res.status === 'accepted') toast.success(`Already connected with ${userName}!`);
      else if (res.status === 'pending') toast.success(`Connection request sent to ${userName}!`);
      else toast.success(res.message || 'Request sent');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to connect'); }
  };

  const handleDM = (userId) => {
    onStartChat(userId);
  };

  const handleShareCard = async (targetUser) => {
    try {
      const newPost = await api.createFeedPost(
        `Check out ${targetUser.full_name}'s digital business card!`,
        'share_card',
        targetUser.id
      );
      setPosts(prev => [newPost, ...prev]);
      setShowShareModal(false);
      setShareSearch('');
      setShareResults([]);
      toast.success('Business card shared to feed!');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to share'); }
  };

  const searchForShare = async () => {
    if (!shareSearch.trim()) return;
    try {
      const data = await api.searchProfessionals({ q: shareSearch });
      setShareResults((data.professionals || []).filter(p => p.id !== user?.id));
    } catch {}
  };

  if (loading) return <div className="flex justify-center py-12"><div className="animate-pulse text-muted-foreground">Loading feed...</div></div>;

  return (
    <div className="max-w-2xl mx-auto space-y-5">
      {/* Compose */}
      <Card data-testid="feed-composer">
        <CardContent className="p-4 space-y-3">
          <textarea
            value={content}
            onChange={e => setContent(e.target.value)}
            placeholder="Share an update, tip, or question with fellow professionals..."
            className="w-full resize-none border border-border rounded-lg p-3 text-sm bg-muted/30 focus:outline-none focus:ring-2 focus:ring-primary/30 min-h-[80px]"
            data-testid="feed-content-input"
            maxLength={1000}
          />
          <div className="flex items-center justify-between">
            <div className="flex gap-1.5 flex-wrap">
              {POST_TYPES.filter(t => t.id !== 'share_card').map(t => (
                <button
                  key={t.id}
                  onClick={() => setPostType(t.id)}
                  data-testid={`post-type-${t.id}`}
                  className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-medium transition-all ${postType === t.id ? t.color + ' ring-2 ring-offset-1 ring-primary/30' : 'bg-muted text-muted-foreground hover:bg-muted/80'}`}
                >
                  <t.icon className="w-3 h-3" />{t.label}
                </button>
              ))}
              <button
                onClick={() => setShowShareModal(true)}
                data-testid="share-card-btn"
                className="flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-medium bg-muted text-muted-foreground hover:bg-teal-100 hover:text-teal-700 transition-all"
              >
                <Share2 className="w-3 h-3" />Share Card
              </button>
            </div>
            <Button size="sm" onClick={handlePost} disabled={posting || !content.trim()} data-testid="feed-post-btn" className="h-8">
              <Send className="w-3.5 h-3.5 mr-1" />{posting ? 'Posting...' : 'Post'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Share Card Modal */}
      {showShareModal && (
        <Card data-testid="share-card-modal">
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold">Share a Business Card</h3>
              <button onClick={() => { setShowShareModal(false); setShareResults([]); }} className="text-muted-foreground hover:text-foreground text-xs">Close</button>
            </div>
            <div className="flex gap-1.5">
              <input
                value={shareSearch}
                onChange={e => setShareSearch(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && searchForShare()}
                placeholder="Search for a professional..."
                className="flex-1 text-xs border border-border rounded-md px-2.5 py-1.5 bg-muted/30 focus:outline-none focus:ring-1 focus:ring-primary/30"
                data-testid="share-search-input"
              />
              <Button size="sm" variant="outline" onClick={searchForShare} className="h-7 text-xs" data-testid="share-search-btn">Search</Button>
            </div>
            {shareResults.map(p => (
              <div key={p.id} className="flex items-center justify-between py-2 border-b border-border/30 last:border-0">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-full bg-primary/20 flex items-center justify-center"><span className="text-[10px] font-bold text-primary">{(p.full_name || 'U')[0]}</span></div>
                  <div>
                    <p className="text-xs font-medium">{p.full_name}</p>
                    <p className="text-[10px] text-muted-foreground">{p.profession_name}</p>
                  </div>
                </div>
                <Button size="sm" variant="outline" className="h-6 text-[10px]" onClick={() => handleShareCard(p)} data-testid={`share-card-select-${p.id}`}>
                  <Share2 className="w-3 h-3 mr-1" />Share
                </Button>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Posts */}
      {posts.length === 0 ? (
        <Card><CardContent className="p-8 text-center text-muted-foreground text-sm">No posts yet. Be the first to share something!</CardContent></Card>
      ) : posts.map(post => {
        const typeInfo = POST_TYPES.find(t => t.id === post.post_type) || POST_TYPES[0];
        const isLiked = (post.likes || []).includes(user?.id);
        const postComments = comments[post.id] || [];
        const isOwnPost = post.user_id === user?.id;
        return (
          <Card key={post.id} data-testid={`feed-post-${post.id}`} className="overflow-hidden">
            <CardContent className="p-0">
              {/* Author header */}
              <div className="flex items-start gap-3 p-4 pb-2">
                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-primary/80 to-primary flex items-center justify-center flex-shrink-0 overflow-hidden">
                  {post.author_photo ? (
                    <img src={resolveImageUrl(post.author_photo)} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-primary-foreground text-sm font-bold">{(post.author_name || 'U')[0]}</span>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <Link
                      to={`/card/${post.user_id}`}
                      className="text-sm font-semibold truncate hover:text-primary transition-colors"
                      data-testid={`post-author-link-${post.id}`}
                    >
                      {post.author_name}
                    </Link>
                    <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${typeInfo.color}`}>{typeInfo.label}</Badge>
                  </div>
                  <div className="flex items-center gap-2 text-[11px] text-muted-foreground mt-0.5">
                    <span>{post.author_profession}</span>
                    {post.author_city && <><span className="opacity-40">|</span><span>{post.author_city}, {post.author_state}</span></>}
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <span className="text-[10px] text-muted-foreground">{timeAgo(post.created_at)}</span>
                  {!isOwnPost && (
                    <div className="flex items-center gap-0.5">
                      <button
                        onClick={() => handleDM(post.user_id)}
                        className="text-muted-foreground hover:text-primary p-1 transition-colors"
                        title="Send Direct Message"
                        data-testid={`dm-btn-${post.id}`}
                      >
                        <MessageSquare className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => handleConnect(post.user_id, post.author_name)}
                        className="text-muted-foreground hover:text-green-600 p-1 transition-colors"
                        title="Connect"
                        data-testid={`connect-btn-${post.id}`}
                      >
                        <UserPlus className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  )}
                  {isOwnPost && (
                    <button onClick={() => handleDelete(post.id)} className="text-muted-foreground hover:text-destructive p-1" data-testid={`delete-post-${post.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                  )}
                </div>
              </div>

              {/* Content */}
              <p className="px-4 pb-3 text-sm whitespace-pre-wrap leading-relaxed">{post.content}</p>

              {/* Shared Card Embed */}
              {post.shared_card && <SharedCardEmbed card={post.shared_card} />}

              {/* Group Promotion Card */}
              {post.group_data && (
                <div className="mx-4 mb-3 rounded-lg border border-violet-200 bg-gradient-to-br from-violet-50 to-indigo-50 overflow-hidden" data-testid={`group-card-${post.group_data.group_id}`}>
                  {(post.group_data.image_url || post.group_data.cover_image) && (
                    <img src={resolveImageUrl(post.group_data.cover_image || post.group_data.image_url)} alt="" className="w-full h-32 object-cover" />
                  )}
                  <div className="p-3">
                    <div className="flex items-center gap-2 mb-1">
                      <Users2 className="w-4 h-4 text-violet-600" />
                      <span className="text-sm font-semibold text-violet-900">{post.group_data.name}</span>
                      {post.group_data.is_private && <Badge variant="outline" className="text-[9px] px-1 py-0">Private</Badge>}
                    </div>
                    {post.group_data.description && <p className="text-xs text-muted-foreground mb-2 line-clamp-2">{post.group_data.description}</p>}
                    <div className="flex flex-wrap gap-3 mb-3 text-xs text-violet-600">
                      {post.group_data.member_count != null && (
                        <span className="flex items-center gap-1"><Users2 className="w-3 h-3" />{post.group_data.member_count} members</span>
                      )}
                      {post.group_data.location && (
                        <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{post.group_data.location}</span>
                      )}
                    </div>
                    <Link to={`/groups/${post.group_data.group_id || post.group_data.id || ''}`} className="block">
                      <Button size="sm" className="w-full bg-violet-600 hover:bg-violet-700 text-white text-xs h-8" data-testid={`group-join-btn-${post.id}`}>
                        <UserPlus className="w-3.5 h-3.5 mr-1.5" />View & Join Group
                      </Button>
                    </Link>
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex items-center border-t border-border/50 px-4 py-2 gap-4">
                <button onClick={() => handleLike(post.id)} className={`flex items-center gap-1.5 text-xs font-medium transition-colors ${isLiked ? 'text-red-500' : 'text-muted-foreground hover:text-red-500'}`} data-testid={`like-btn-${post.id}`}>
                  <Heart className={`w-3.5 h-3.5 ${isLiked ? 'fill-current' : ''}`} />{(post.likes || []).length}
                </button>
                <button onClick={() => toggleComments(post.id)} className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-primary transition-colors" data-testid={`comment-btn-${post.id}`}>
                  <MessageCircle className="w-3.5 h-3.5" />{post.comment_count || 0}
                </button>
              </div>

              {/* Comments section */}
              {expandedComments[post.id] && (
                <div className="border-t border-border/50 bg-muted/20 px-4 py-3 space-y-3">
                  {postComments.map(c => (
                    <div key={c.id} className="flex gap-2" data-testid={`comment-${c.id}`}>
                      <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                        <span className="text-[10px] font-bold text-primary">{(c.author_name || 'U')[0]}</span>
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-semibold">{c.author_name}</span>
                          <span className="text-[10px] text-muted-foreground">{timeAgo(c.created_at)}</span>
                        </div>
                        <p className="text-xs text-foreground/80 mt-0.5">{c.content}</p>
                      </div>
                    </div>
                  ))}
                  <div className="flex gap-2">
                    <input
                      value={commentText[post.id] || ''}
                      onChange={e => setCommentText(prev => ({ ...prev, [post.id]: e.target.value }))}
                      onKeyDown={e => e.key === 'Enter' && handleComment(post.id)}
                      placeholder="Write a comment..."
                      className="flex-1 text-xs border border-border rounded-md px-3 py-1.5 bg-background focus:outline-none focus:ring-1 focus:ring-primary/30"
                      data-testid={`comment-input-${post.id}`}
                    />
                    <Button size="sm" variant="ghost" onClick={() => handleComment(post.id)} className="h-7 px-2"><Send className="w-3 h-3" /></Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}

      {hasMore && (
        <div className="text-center">
          <Button variant="outline" size="sm" onClick={() => fetchFeed(page + 1)} data-testid="load-more-feed">
            <ChevronDown className="w-3.5 h-3.5 mr-1" />Load More
          </Button>
        </div>
      )}
    </div>
  );
};

// ==================== CHAT TAB ====================
const ChatTab = ({ initialUserId }) => {
  const { user } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [activeConvo, setActiveConvo] = useState(null);
  const [messages, setMessages] = useState([]);
  const [msgText, setMsgText] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const messagesEndRef = useRef(null);
  const pollRef = useRef(null);

  const fetchConversations = useCallback(async () => {
    try {
      const data = await api.getConversations();
      setConversations(data.conversations || []);
    } catch {}
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchConversations(); }, [fetchConversations]);

  // Auto-open a conversation if initialUserId is provided
  useEffect(() => {
    if (initialUserId && !loading) {
      const startChat = async () => {
        try {
          const convo = await api.createConversation(initialUserId);
          await fetchConversations();
          openConversation(convo);
        } catch {}
      };
      startChat();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialUserId, loading]);

  const openConversation = async (convo) => {
    setActiveConvo(convo);
    try {
      const data = await api.getMessages(convo.id);
      setMessages(data.messages || []);
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    } catch { toast.error('Failed to load messages'); }

    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const data = await api.getMessages(convo.id);
        setMessages(data.messages || []);
      } catch {}
    }, 5000);
  };

  useEffect(() => {
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  const handleSend = async () => {
    if (!msgText.trim() || !activeConvo) return;
    setSending(true);
    try {
      const msg = await api.sendMessage(activeConvo.id, msgText);
      setMessages(prev => [...prev, msg]);
      setMsgText('');
      setConversations(prev => prev.map(c => c.id === activeConvo.id ? { ...c, last_message: msg.content, last_message_at: msg.created_at } : c));
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
    } catch { toast.error('Failed to send'); }
    finally { setSending(false); }
  };

  const startNewChat = async (otherUserId) => {
    try {
      const convo = await api.createConversation(otherUserId);
      setSearchQuery('');
      setSearchResults([]);
      await fetchConversations();
      openConversation(convo);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to start chat'); }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    setSearching(true);
    try {
      const data = await api.searchProfessionals({ q: searchQuery });
      setSearchResults((data.professionals || []).filter(p => p.id !== user?.id));
    } catch {}
    finally { setSearching(false); }
  };

  const getOtherParticipant = (convo) => {
    if (!convo?.participant_info || !user) return { name: 'Unknown', profession: '' };
    const otherId = convo.participants?.find(p => p !== user.id);
    return convo.participant_info[otherId] || { name: 'Unknown', profession: '' };
  };

  if (loading) return <div className="flex justify-center py-12"><div className="animate-pulse text-muted-foreground">Loading messages...</div></div>;

  return (
    <div className="flex h-[calc(100vh-12rem)] border border-border rounded-lg overflow-hidden bg-background" data-testid="chat-container">
      {/* Conversation List */}
      <div className={`${activeConvo ? 'hidden md:flex' : 'flex'} flex-col w-full md:w-80 border-r border-border`}>
        <div className="p-3 border-b border-border space-y-2">
          <h3 className="text-sm font-semibold">Messages</h3>
          <div className="flex gap-1.5">
            <input
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSearch()}
              placeholder="Find a professional..."
              className="flex-1 text-xs border border-border rounded-md px-2.5 py-1.5 bg-muted/30 focus:outline-none focus:ring-1 focus:ring-primary/30"
              data-testid="chat-search-input"
            />
            <Button size="sm" variant="outline" onClick={handleSearch} disabled={searching} className="h-7 px-2 text-xs" data-testid="chat-search-btn">Search</Button>
          </div>
        </div>

        {searchResults.length > 0 && (
          <div className="border-b border-border bg-muted/20 max-h-40 overflow-auto">
            {searchResults.map(p => (
              <button key={p.id} onClick={() => startNewChat(p.id)} className="w-full flex items-center gap-2 px-3 py-2 hover:bg-muted/50 text-left" data-testid={`search-result-${p.id}`}>
                <div className="w-7 h-7 rounded-full bg-primary/20 flex items-center justify-center"><User className="w-3.5 h-3.5 text-primary" /></div>
                <div><p className="text-xs font-medium">{p.full_name}</p><p className="text-[10px] text-muted-foreground">{p.profession_name}</p></div>
              </button>
            ))}
          </div>
        )}

        <div className="flex-1 overflow-auto">
          {conversations.length === 0 ? (
            <p className="p-4 text-xs text-muted-foreground text-center">No conversations yet. Search for a professional to start chatting.</p>
          ) : conversations.map(c => {
            const other = getOtherParticipant(c);
            return (
              <button key={c.id} onClick={() => openConversation(c)} className={`w-full flex items-center gap-2.5 px-3 py-3 hover:bg-muted/50 border-b border-border/30 transition-colors ${activeConvo?.id === c.id ? 'bg-primary/5' : ''}`} data-testid={`convo-${c.id}`}>
                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-primary/60 to-primary flex items-center justify-center flex-shrink-0 overflow-hidden">
                  {other.photo ? <img src={resolveImageUrl(other.photo)} alt="" className="w-full h-full object-cover" /> : <span className="text-primary-foreground text-sm font-bold">{(other.name || 'U')[0]}</span>}
                </div>
                <div className="flex-1 min-w-0 text-left">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold truncate">{other.name}</span>
                    <span className="text-[10px] text-muted-foreground">{timeAgo(c.last_message_at)}</span>
                  </div>
                  <p className="text-[11px] text-muted-foreground truncate">{c.last_message || 'No messages yet'}</p>
                </div>
                {c.unread_count > 0 && <span className="bg-primary text-primary-foreground text-[10px] rounded-full w-4 h-4 flex items-center justify-center font-bold">{c.unread_count}</span>}
              </button>
            );
          })}
        </div>
      </div>

      {/* Message Area */}
      <div className={`${activeConvo ? 'flex' : 'hidden md:flex'} flex-col flex-1`}>
        {activeConvo ? (
          <>
            <div className="flex items-center gap-3 p-3 border-b border-border">
              <button onClick={() => { setActiveConvo(null); if (pollRef.current) clearInterval(pollRef.current); }} className="md:hidden text-muted-foreground"><ChevronDown className="w-4 h-4 rotate-90" /></button>
              <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center"><span className="text-sm font-bold text-primary">{(getOtherParticipant(activeConvo).name || 'U')[0]}</span></div>
              <div><p className="text-sm font-semibold">{getOtherParticipant(activeConvo).name}</p><p className="text-[11px] text-muted-foreground">{getOtherParticipant(activeConvo).profession}</p></div>
            </div>
            <div className="flex-1 overflow-auto p-4 space-y-3" data-testid="messages-area">
              {messages.map(m => (
                <div key={m.id} className={`flex ${m.sender_id === user?.id ? 'justify-end' : 'justify-start'}`} data-testid={`msg-${m.id}`}>
                  <div className={`max-w-[75%] px-3 py-2 rounded-xl text-sm ${m.sender_id === user?.id ? 'bg-primary text-primary-foreground rounded-br-sm' : 'bg-muted rounded-bl-sm'}`}>
                    <p className="whitespace-pre-wrap">{m.content}</p>
                    <p className={`text-[10px] mt-1 ${m.sender_id === user?.id ? 'text-primary-foreground/60' : 'text-muted-foreground/60'}`}>{timeAgo(m.created_at)}</p>
                  </div>
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>
            <div className="p-3 border-t border-border flex gap-2">
              <input value={msgText} onChange={e => setMsgText(e.target.value)} onKeyDown={e => e.key === 'Enter' && !e.shiftKey && handleSend()} placeholder="Type a message..." className="flex-1 text-sm border border-border rounded-lg px-3 py-2 bg-muted/30 focus:outline-none focus:ring-2 focus:ring-primary/30" data-testid="chat-message-input" />
              <Button onClick={handleSend} disabled={sending || !msgText.trim()} data-testid="chat-send-btn" className="h-9 px-3"><Send className="w-4 h-4" /></Button>
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center text-muted-foreground">
            <div className="text-center"><MessageSquare className="w-10 h-10 mx-auto mb-3 opacity-30" /><p className="text-sm">Select a conversation or search for a professional to start chatting</p></div>
          </div>
        )}
      </div>
    </div>
  );
};

// ==================== CONNECTIONS TAB ====================
const ConnectionsTab = ({ onStartChat }) => {
  const [connections, setConnections] = useState({ accepted: [], pending_sent: [], pending_received: [] });
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState({});
  const [section, setSection] = useState('received');

  const fetchConnections = useCallback(async () => {
    try {
      const data = await api.getConnections();
      setConnections(data);
    } catch { toast.error('Failed to load connections'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchConnections(); }, [fetchConnections]);

  const handleAccept = async (connId, name) => {
    setProcessing(prev => ({ ...prev, [connId]: 'accepting' }));
    try {
      await api.acceptConnection(connId);
      toast.success(`Connected with ${name}!`);
      fetchConnections();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to accept'); }
    finally { setProcessing(prev => ({ ...prev, [connId]: null })); }
  };

  const handleDecline = async (connId, name) => {
    setProcessing(prev => ({ ...prev, [connId]: 'declining' }));
    try {
      await api.declineConnection(connId);
      toast.success(`Declined request from ${name}`);
      fetchConnections();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to decline'); }
    finally { setProcessing(prev => ({ ...prev, [connId]: null })); }
  };

  const receivedCount = connections.pending_received.length;
  const sentCount = connections.pending_sent.length;
  const acceptedCount = connections.accepted.length;

  if (loading) return <div className="flex justify-center py-12"><div className="animate-pulse text-muted-foreground">Loading connections...</div></div>;

  const sections = [
    { id: 'received', label: 'Requests', count: receivedCount },
    { id: 'sent', label: 'Sent', count: sentCount },
    { id: 'accepted', label: 'Connected', count: acceptedCount },
  ];

  const PersonCard = ({ entry, actions }) => (
    <div className="flex items-center gap-3 p-3 rounded-lg border border-border/50 bg-background hover:shadow-sm transition-shadow" data-testid={`connection-card-${entry.user_id}`}>
      <Link to={`/card/${entry.user_id}`} className="w-10 h-10 rounded-full bg-gradient-to-br from-primary/60 to-primary flex items-center justify-center flex-shrink-0 overflow-hidden">
        {entry.photo ? (
          <img src={resolveImageUrl(entry.photo)} alt="" className="w-full h-full object-cover" />
        ) : (
          <span className="text-primary-foreground text-sm font-bold">{(entry.name || 'U')[0]}</span>
        )}
      </Link>
      <div className="flex-1 min-w-0">
        <Link to={`/card/${entry.user_id}`} className="text-sm font-semibold hover:text-primary transition-colors truncate block" data-testid={`conn-name-${entry.user_id}`}>
          {entry.name}
        </Link>
        <p className="text-xs text-muted-foreground truncate">{entry.profession}{entry.company ? ` at ${entry.company}` : ''}</p>
        {(entry.city || entry.state) && (
          <p className="text-[11px] text-muted-foreground/70 flex items-center gap-0.5 mt-0.5">
            <MapPin className="w-2.5 h-2.5" />{[entry.city, entry.state].filter(Boolean).join(', ')}
          </p>
        )}
      </div>
      <div className="flex items-center gap-1.5 flex-shrink-0">{actions}</div>
    </div>
  );

  return (
    <div className="max-w-2xl mx-auto space-y-4">
      {/* Section tabs */}
      <div className="flex gap-1 bg-muted/40 p-1 rounded-lg" data-testid="connections-sections">
        {sections.map(s => (
          <button
            key={s.id}
            onClick={() => setSection(s.id)}
            data-testid={`conn-section-${s.id}`}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all flex-1 justify-center ${section === s.id ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
          >
            {s.label}
            {s.count > 0 && (
              <span className={`text-[10px] rounded-full px-1.5 py-0.5 font-bold ${section === s.id ? (s.id === 'received' ? 'bg-primary text-primary-foreground' : 'bg-muted text-foreground') : 'bg-muted/80 text-muted-foreground'}`}>
                {s.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Pending Received */}
      {section === 'received' && (
        <div className="space-y-2" data-testid="pending-received-list">
          {receivedCount === 0 ? (
            <Card><CardContent className="p-6 text-center text-muted-foreground text-sm">
              <UserPlus className="w-8 h-8 mx-auto mb-2 opacity-30" />
              No pending connection requests
            </CardContent></Card>
          ) : connections.pending_received.map(entry => (
            <PersonCard
              key={entry.connection_id}
              entry={entry}
              actions={<>
                <Button
                  size="sm"
                  onClick={() => handleAccept(entry.connection_id, entry.name)}
                  disabled={!!processing[entry.connection_id]}
                  data-testid={`accept-btn-${entry.user_id}`}
                  className="h-7 text-xs gap-1"
                >
                  {processing[entry.connection_id] === 'accepting' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                  Accept
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleDecline(entry.connection_id, entry.name)}
                  disabled={!!processing[entry.connection_id]}
                  data-testid={`decline-btn-${entry.user_id}`}
                  className="h-7 text-xs gap-1 text-muted-foreground hover:text-destructive hover:border-destructive"
                >
                  {processing[entry.connection_id] === 'declining' ? <Loader2 className="w-3 h-3 animate-spin" /> : <X className="w-3 h-3" />}
                  Decline
                </Button>
              </>}
            />
          ))}
        </div>
      )}

      {/* Pending Sent */}
      {section === 'sent' && (
        <div className="space-y-2" data-testid="pending-sent-list">
          {sentCount === 0 ? (
            <Card><CardContent className="p-6 text-center text-muted-foreground text-sm">
              <Send className="w-8 h-8 mx-auto mb-2 opacity-30" />
              No pending sent requests
            </CardContent></Card>
          ) : connections.pending_sent.map(entry => (
            <PersonCard
              key={entry.connection_id}
              entry={entry}
              actions={
                <Badge variant="outline" className="text-[10px] text-amber-600 border-amber-300 bg-amber-50" data-testid={`pending-badge-${entry.user_id}`}>
                  <Clock className="w-2.5 h-2.5 mr-0.5" />Pending
                </Badge>
              }
            />
          ))}
        </div>
      )}

      {/* Accepted */}
      {section === 'accepted' && (
        <div className="space-y-2" data-testid="accepted-connections-list">
          {acceptedCount === 0 ? (
            <Card><CardContent className="p-6 text-center text-muted-foreground text-sm">
              <Users className="w-8 h-8 mx-auto mb-2 opacity-30" />
              No connections yet. Connect with professionals from the Feed!
            </CardContent></Card>
          ) : connections.accepted.map(entry => (
            <PersonCard
              key={entry.connection_id}
              entry={entry}
              actions={<>
                <button
                  onClick={() => onStartChat(entry.user_id)}
                  className="text-muted-foreground hover:text-primary p-1.5 rounded-md hover:bg-muted transition-colors"
                  title="Send Message"
                  data-testid={`conn-dm-btn-${entry.user_id}`}
                >
                  <MessageSquare className="w-4 h-4" />
                </button>
                <Link
                  to={`/card/${entry.user_id}`}
                  className="text-muted-foreground hover:text-primary p-1.5 rounded-md hover:bg-muted transition-colors"
                  title="View Profile"
                  data-testid={`conn-profile-btn-${entry.user_id}`}
                >
                  <ExternalLink className="w-4 h-4" />
                </Link>
              </>}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// ==================== MAIN PAGE ====================
export default function CommunityHub() {
  const [tab, setTab] = useState('feed');
  const [chatUserId, setChatUserId] = useState(null);

  const handleStartChat = (userId) => {
    setChatUserId(userId);
    setTab('chat');
  };

  return (
    <Layout>
      <div className="space-y-5" data-testid="community-hub">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Community</h1>
            <p className="text-sm text-muted-foreground mt-0.5">Connect and share with fellow professionals</p>
          </div>
        </div>

        <div className="flex gap-1 bg-muted/50 p-1 rounded-lg w-fit" data-testid="community-tabs">
          <button onClick={() => setTab('feed')} data-testid="tab-feed" className={`flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium transition-all ${tab === 'feed' ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'}`}>
            <TrendingUp className="w-4 h-4" />Feed
          </button>
          <button onClick={() => setTab('chat')} data-testid="tab-chat" className={`flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium transition-all ${tab === 'chat' ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'}`}>
            <MessageCircle className="w-4 h-4" />Messages
          </button>
          <button onClick={() => setTab('connections')} data-testid="tab-connections" className={`flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium transition-all ${tab === 'connections' ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'}`}>
            <Users className="w-4 h-4" />Connections
          </button>
        </div>

        {tab === 'feed' && <FeedTab onStartChat={handleStartChat} />}
        {tab === 'chat' && <ChatTab initialUserId={chatUserId} />}
        {tab === 'connections' && <ConnectionsTab onStartChat={handleStartChat} />}
      </div>
    </Layout>
  );
}
